import { UseControllerProps } from 'react-hook-form';

import { InputSize } from '@xemplo/input-utils';
import type { V2Types } from '@xemplo/popover';
import { ListValue } from '@xemplo/stateful-list-core';
import { IntegerRange, Prettify } from '@xemplo/types';
import { XemploInputProps } from '@xemplo/xemplo-input';

export const TimePickerSize = {
  Spacious: InputSize.Spacious,
  Default: InputSize.Default,
  Compact: InputSize.Compact,
} as const;
export type TimePickerSize = (typeof TimePickerSize)[keyof typeof TimePickerSize];

export type TimeInputType = '12-hour' | '24-hour';
export type HourOfDay = IntegerRange<0, 23>;

export type TimeListOpt = {
  /**
   * Whether the input computes 12 or 24 hrs format
   * @default '12-hour'
   */
  timeInputType?: TimeInputType;

  /**
   * Use it to customize the start time of the time picker list.
   * Must be a valid hour in 24-hour format.
   *
   * @default 7
   */
  startHour?: HourOfDay;

  /**
   * Use it to customize the end time of the time picker list.
   * Must be a valid hour in 24-hour format.
   *
   * @default 20
   */
  endHour?: HourOfDay;

  /**
   * The interval in minutes between each time option.
   * Must be one of the following: 1, 5, 10, 15, 20, or 30.
   *
   * @default 30
   */
  increment?: 1 | 5 | 10 | 15 | 20 | 30;
};

type BaseInputProps = Omit<XemploInputProps, 'type' | 'inputSize' | 'onChange' | 'value'>;
type BasePopoverProps = Omit<V2Types.PopoverProps, 'trigger'>;
type BaseTimePickerProps = {
  /**
   * The visual size of the input element.
   * @default TimePickerSize.Medium
   */
  inputSize?: TimePickerSize;

  /** Controlled input value */
  value?: ListValue;

  /** The event handler triggered by the HTMLInputElement.onChange */
  onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

  /** The event handler triggered by the List.onChange event */
  onListChange?: (value: ListValue | undefined) => void;
};

export type TimePickerProps = BaseInputProps &
  Prettify<BasePopoverProps & TimeListOpt & BaseTimePickerProps>;

/**
 * The time picker props specifically for react-hook-form wrapper.
 * NOTE: I'm deliberatly omitting `value`, `onInputChange`,
 * and `onListChange` as they are handled by the controller.
 *
 * In the future, if you need to expose those events, do it
 * carefully as they control how the control works with the form.
 */
export type TimePickerRhfProps = Omit<
  TimePickerProps,
  'value' | 'onInputChange' | 'onListChange'
> & {
  /**
   * Validation rules for the input.
   * NOTE: There's a built-in validation rule for invalid time.
   * This will be merged with the provided validation.
   *
   * If you are using a custom validator, you will need to
   * import the invalidTime function from the helper
   * and use it in your validation rules.
   * Example:
   * ```ts
   * rules={{
   *   validate: (value) => {
   *     // your custom validation logic
   *     ...
   *     // then check for invalid time
   *     if (invalidTime(value)) {
   *       return 'Invalid time format';
   *    }
   *    return true;
   *  },
   */
  rules?: UseControllerProps['rules'];

  /** Change event that exposes the input value */
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: ListValue | undefined
  ) => void;
};
